// src/components/RedemptionCards.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import './RedemptionCards.css';
import { QRCodeSVG } from 'qrcode.react';
import { useParams } from 'react-router-dom';

const RedemptionCards = () => {
  const { schoolId } = useParams(); // e.g. /nlcs/...; may be undefined on legacy routes
  const [schoolName, setSchoolName] = useState('PBIS Rewards');
  const [studentsByClass, setStudentsByClass] = useState({});
  const [ticketBalances, setTicketBalances] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      // ----- School display name -----
      if (schoolId) {
        try {
          const sSnap = await getDoc(doc(db, 'schools', schoolId));
          if (sSnap.exists()) {
            setSchoolName(sSnap.data().name || 'PBIS Rewards');
          } else {
            setSchoolName('PBIS Rewards');
          }
        } catch {
          setSchoolName('PBIS Rewards');
        }
      } else {
        setSchoolName('PBIS Rewards');
      }

      // ----- Users (students) -----
      try {
        const usersCol = collection(db, 'users');
        let usersSnap;

        if (schoolId) {
          usersSnap = await getDocs(query(usersCol, where('schoolId', '==', schoolId)));
          if (usersSnap.empty) {
            usersSnap = await getDocs(usersCol); // fallback
          }
        } else {
          usersSnap = await getDocs(usersCol);
        }

        const grouped = {};
        usersSnap.forEach((docSnap) => {
          const student = docSnap.data();
          if (student.role !== 'student') return;
          const klass = student.class ?? student.advisory ?? 'Unassigned';
          if (!grouped[klass]) grouped[klass] = [];
          grouped[klass].push({ id: docSnap.id, ...student });
        });

        const sorted = {};
        Object.keys(grouped)
          .sort((a, b) => a.localeCompare(b))
          .forEach((klass) => {
            sorted[klass] = grouped[klass].sort((a, b) =>
              (a.name || '').localeCompare(b.name || '')
            );
          });

        setStudentsByClass(sorted);
      } catch (e) {
        console.error('Error loading users for redemption cards:', e);
      }

      // ----- Tickets (balances) -----
      try {
        const ticketsCol = collection(db, 'schools', schoolId, 'tickets'); // ✅ Scoped path
        const ticketsSnap = await getDocs(ticketsCol);

        const balanceMap = {};
        ticketsSnap.forEach((docSnap) => {
          const ticket = docSnap.data();
          const studentId = ticket.studentId;
          const quantity = ticket.quantity ?? 1;
          if (!studentId) return;
          balanceMap[studentId] = (balanceMap[studentId] || 0) + quantity;
        });

        setTicketBalances(balanceMap);
      } catch (e) {
        console.error('Error loading tickets for redemption cards:', e);
      }
    };

    fetchData();
  }, [schoolId]);

  const handlePrint = () => {
    setTimeout(() => window.print(), 500);
  };

  const storeModePath = (studentId) => {
    const base = window.location.origin;
    const path = schoolId
      ? `/${schoolId}/store-mode?id=${studentId}`
      : `/store-mode?id=${studentId}`;
    return `${base}${path}`;
  };

  return (
    <div className="redemption-page">
      <div className="print-header">
        <h1>Redemption Cards</h1>
        <button onClick={handlePrint}>Print</button>
      </div>

      {Object.entries(studentsByClass).map(([klass, students]) => (
        <section key={klass} className="class-section">
          <h2>{klass} Class</h2>
          <div className="card-grid">
            {students.map((student) => {
              const balance = ticketBalances[student.id] || 0;
              return (
                <div key={student.id} className="redemption-card">
                  <h3>{schoolName} — Rewards Redemption Card</h3>
                  <p><strong>Student Name:</strong> {student.name || '___________'}</p>
                  <p><strong>Ticket Total:</strong> {balance}</p>
                  <p><input type="checkbox" readOnly /> Gift Card: __________</p>
                  <p><input type="checkbox" readOnly /> Clothing: __________</p>
                  <div className="qrcode-container">
                    <QRCodeSVG value={storeModePath(student.id)} size={256} includeMargin={true} />
                    <div>Scan to Open Store Profile</div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
};

export default RedemptionCards;